import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import MultiQueryPageLoader from '@/app/common/components/MultiQueryPageLoader'
import { useValidatedParams } from '@/app/common/hooks'
import { Card, CardContent, CardHeader } from '@/common/components/Card'
import { ProviderPermissionManagementQueries } from '@/queries/provider-permission-management/queries'
import {
  ProviderUserRole,
  ProviderUserRoleCode,
} from '@/queries/provider-permission-management/schema'
import { ProviderQueries } from '@/queries/providers/queries'
import { Label } from '@/shad/ui/label'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { selectUser } from '@/storage/user/authSlice'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { ProviderUserManagementParams } from '../schema'
import ProviderUsersTable from './_components/provider-user-role-table/provider-user-table'
import CreateUserDialog from './manage-user-form/create-user-dialog'
import AdminAuthGuard from '../admin-auth-guard'

function getAllowedRoles(
  authenticatedUserRoles: string[],
  providerUserRoles: ProviderUserRole[]
) {
  return providerUserRoles.filter((role) => {
    // This logic is based on this diagram here:
    // https://app.clickup.com/t/86ad79bm6?comment=90130197705606&threadedComment=90130201763947
    const hasFullEditPermission =
      authenticatedUserRoles.includes('admin') ||
      authenticatedUserRoles.includes('program_manager')

    if (hasFullEditPermission) {
      return role
    }

    // Only admin and program manager can assign these roles.
    const isRestrictedProviderUserRole: ProviderUserRoleCode[] = [
      'OWNER',
      'ADMIN',
      'FINANCE',
    ]

    if (isRestrictedProviderUserRole.includes(role.role_code)) {
      return false
    }
    return true
  })
}

export default function ManageUsersPage() {
  const programId = useSelector(selectSelectedProgram).id
  const { roles } = useSelector(selectUser)

  const { providerId } = useValidatedParams(ProviderUserManagementParams)

  const providerDetailsQuery = useQuery(
    ProviderQueries.getProviderById(programId, providerId)
  )
  const providerUsersQuery = useQuery(
    ProviderPermissionManagementQueries.getProviderUsers(providerId)
  )

  const rolesQuery = useQuery({
    ...ProviderPermissionManagementQueries.getAllRoles(),
    select: (data) => {
      // First apply the original OWNER filter, then apply role-based permissions
      const rolesWithoutOwner = data.filter(
        (role) => role.role_code !== 'OWNER'
      )
      return getAllowedRoles(roles, rolesWithoutOwner)
    },
  })

  const [searchFilter, setSearchFilter] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  return (
    <MultiQueryPageLoader
      queries={[providerDetailsQuery, providerUsersQuery, rolesQuery] as const}
    >
      {([providerData, providerUsers, roles]) => {
        return (
          <div className="p-4 space-y-4 max-w-7xl mx-auto w-full">
            <Card>
              <CardContent className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {providerData.logo ? (
                    <img
                      src={providerData.logo}
                      alt={providerData.name ?? ''}
                      className="w-12 h-12 rounded-full"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <UserIcon className="w-6 h-6 text-gray-400" />
                    </div>
                  )}

                  <div>
                    <div className="font-medium text-lg capitalize text-gray-800">
                      {providerData.name}
                    </div>
                    <div className="text-gray-600 text-sm">
                      {providerData.city}, {providerData.global_state_name}
                    </div>
                  </div>
                </div>
                <AdminAuthGuard
                  requiredPermissions={[
                    'provider_user_roles_management_create',
                  ]}
                >
                  <Button
                    onClick={() => {
                      setCreateDialogOpen(true)
                    }}
                  >
                    Add New User <PlusIcon className="size-4 ml-2" />
                  </Button>
                </AdminAuthGuard>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex justify-between items-center text-md font-semibold py-3">
                <div className="text-left text-lg md:text-md text-gray-900">
                  Manage Users
                </div>
                <div>
                  <Label>Search Users</Label>
                  <Input
                    placeholder="Search Users"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
                <ProviderUsersTable
                  users={providerUsers}
                  providerId={providerId}
                  roles={roles}
                  searchFilter={searchFilter}
                />
              </CardContent>
            </Card>
            <CreateUserDialog
              roles={roles}
              open={createDialogOpen}
              onOpenChange={setCreateDialogOpen}
              providerId={providerId}
            />
          </div>
        )
      }}
    </MultiQueryPageLoader>
  )
}
